import { useEffect, useState, useCallback } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { eur, dateIt } from "@/lib/format";
import { Plus, Pencil, Trash2, Download, Wallet, KeyRound } from "lucide-react";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import { PartnerPricingPolicy, PRICING_MODES } from "@/components/app/PartnerPricingPolicy";

const txLabel = { assegnazione: "Assegnazione", rettifica: "Rettifica", utilizzo: "Utilizzo", ripristino: "Ripristino", ricarica_pacchetto: "Ricarica pacchetto", storno_ricarica: "Storno ricarica" };
const txTone = {
  assegnazione: "bg-emerald-100 text-emerald-700",
  rettifica: "bg-indigo-100 text-indigo-700",
  utilizzo: "bg-amber-100 text-amber-700",
  ripristino: "bg-sky-100 text-sky-700",
  ricarica_pacchetto: "bg-emerald-100 text-emerald-700",
  storno_ricarica: "bg-red-100 text-red-700",
};

const empty = {
  company_name: "", vat_number: "", address: "", contact_name: "", email: "", phone: "",
  discount_percent: 0,
  discount_elearning_percent: 0,
  discount_videoconferenza_percent: 0,
  discount_aula_percent: 0,
  pricing_mode: "none",
  custom_prices: {},
  active: true, password: ""
};

const pctLabel = (v) => (v === null || v === undefined || v === "") ? "—" : `${v}%`;
const modeLabel = Object.fromEntries(PRICING_MODES);
const cleanPrices = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([k, v]) => [k, Number(v)]));

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditPartner, setCreditPartner] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [creditForm, setCreditForm] = useState({ op: "assign", amount: 0, notes: "" });
  const [pwTarget, setPwTarget] = useState(null);
  const [courses, setCourses] = useState([]);

  const load = useCallback(() => api.get("/partners").then(r => setPartners(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);

  const openCredit = async (p) => {
    setCreditPartner(p); setCreditOpen(true); setCreditData(null);
    setCreditForm({ op: "assign", amount: 0, notes: "" });
    try {
      const { data } = await api.get(`/partners/${p.id}/credit`);
      setCreditData(data);
    } catch (e) { toast.error("Impossibile caricare il credito"); }
  };
  const reloadCredit = async (pid) => {
    const { data } = await api.get(`/partners/${pid}/credit`);
    setCreditData(data);
    load();
  };
  const submitCredit = async () => {
    const amt = parseFloat(creditForm.amount || 0);
    if (!amt || Math.abs(amt) < 0.01) { toast.error("Importo richiesto"); return; }
    try {
      if (creditForm.op === "assign") {
        if (amt <= 0) { toast.error("L'assegnazione richiede importo positivo"); return; }
        await api.post(`/partners/${creditPartner.id}/credit/assign`, { amount: amt, notes: creditForm.notes });
        toast.success(`Credito assegnato: ${eur(amt)}`);
      } else {
        if (!creditForm.notes?.trim()) { toast.error("La rettifica richiede una nota"); return; }
        await api.post(`/partners/${creditPartner.id}/credit/adjust`, { delta: amt, notes: creditForm.notes });
        toast.success(`Rettifica applicata: ${amt > 0 ? "+" : ""}${eur(amt)}`);
      }
      setCreditForm({ op: creditForm.op, amount: 0, notes: "" });
      await reloadCredit(creditPartner.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const save = async () => {
    const payload = { ...form, custom_prices: cleanPrices(form.custom_prices) };
    try {
      if (editing) {
        const { password, ...rest } = payload;
        await api.put(`/partners/${editing}`, rest);
        toast.success("Partner aggiornato");
      } else {
        const { data } = await api.post("/partners", payload);
        toast.success(`Partner creato · Password temporanea: ${data.temporary_password}`);
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio del partner"); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare il partner e utente collegato?")) return; await api.delete(`/partners/${id}`); toast.success("Partner eliminato"); load(); };
  const edit = (p) => { setEditing(p.id); setForm({ ...empty, ...p, pricing_mode: p.pricing_mode || "percent", custom_prices: p.custom_prices || {} }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="admin-partners">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Gestione partner</h1>
          <p className="text-slate-500 mt-1">Anagrafiche affiliati, listini dedicati, scontistica personalizzata.</p>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/export/partners`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline"><Download className="w-4 h-4 mr-1" /> Esporta</Button>
          </a>
          <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="new-partner-button">
            <Plus className="w-4 h-4 mr-1" /> Nuovo partner
          </Button>
        </div>
      </div>
      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Ragione sociale</TableHead><TableHead>Referente</TableHead>
            <TableHead>Email</TableHead><TableHead>P.IVA</TableHead>
            <TableHead>Politica commerciale</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {partners.map((p) => (
              <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                <TableCell className="font-medium">{p.company_name}</TableCell>
                <TableCell>{p.contact_name}</TableCell>
                <TableCell>{p.email}</TableCell>
                <TableCell className="font-mono text-xs">{p.vat_number}</TableCell>
                <TableCell className="text-xs" data-testid={`partner-discounts-${p.id}`}>
                  <div className="font-semibold text-slate-700 mb-1" data-testid={`partner-pricing-mode-${p.id}`}>{modeLabel[p.pricing_mode || "percent"]}</div>
                  {(p.pricing_mode || "percent") === "percent" && <span className="inline-flex gap-1.5">
                    <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">E-learn {pctLabel(p.discount_elearning_percent)}</span>
                    <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">Video {pctLabel(p.discount_videoconferenza_percent)}</span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Aula {pctLabel(p.discount_aula_percent)}</span>
                  </span>}
                  {p.pricing_mode === "custom" && <span className="text-slate-500">{(() => { const n = Object.keys(p.custom_prices || {}).length; return `${n} ${n === 1 ? "corso" : "corsi"} con prezzo dedicato`; })()}</span>}
                </TableCell>
                <TableCell><span className={`status-pill ${p.active ? "status-confermato" : "status-bozza"}`}>{p.active ? "Attivo" : "Sospeso"}</span></TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button variant="outline" size="sm" onClick={() => openCredit(p)} data-testid={`credit-partner-${p.id}`} className="mr-1">
                    <Wallet className="w-4 h-4 mr-1" />
                    <span className="text-xs font-mono" data-testid={`partner-credit-balance-${p.id}`}>{eur(p.credit_balance || 0)}</span>
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`edit-partner-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" title="Cambia password" onClick={() => setPwTarget({ type: "partner", id: p.id, name: p.company_name, email: p.email })} data-testid={`partner-password-${p.id}`}><KeyRound className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`delete-partner-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="credit-dialog">
          <DialogHeader><DialogTitle>Credito Partner · {creditPartner?.company_name}</DialogTitle></DialogHeader>
          {!creditData ? <div className="text-slate-500 py-6">Caricamento…</div> : (
            <Tabs defaultValue="overview">
              <TabsList><TabsTrigger value="overview">Panoramica</TabsTrigger><TabsTrigger value="movements">Movimentazioni</TabsTrigger></TabsList>
              <TabsContent value="overview">
                <div className="grid grid-cols-3 gap-3 my-4">
                  <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="p-4">
                    <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Residuo</div>
                    <div className="font-display text-2xl font-extrabold mt-1 text-emerald-700" data-testid="credit-dialog-balance">{eur(creditData.credit_balance)}</div>
                  </CardContent></Card>
                  <Card className="border-amber-200 bg-amber-50/40"><CardContent className="p-4">
                    <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">Utilizzato</div>
                    <div className="font-display text-2xl font-extrabold mt-1 text-amber-700">{eur(creditData.credit_used)}</div>
                  </CardContent></Card>
                  <Card className="border-indigo-200 bg-indigo-50/40"><CardContent className="p-4">
                    <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Iniziale</div>
                    <div className="font-display text-2xl font-extrabold mt-1 text-indigo-700">{eur(creditData.credit_initial)}</div>
                  </CardContent></Card>
                </div>
                <div className="border rounded-xl p-4 space-y-3">
                  <div className="font-semibold text-slate-700">Nuova operazione</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Operazione</Label>
                      <select className="w-full h-10 border rounded-md px-2 bg-white"
                        value={creditForm.op}
                        onChange={(e) => setCreditForm({ ...creditForm, op: e.target.value })}
                        data-testid="credit-op-select">
                        <option value="assign">Assegnazione (+)</option>
                        <option value="adjust">Rettifica (+/−)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Importo (€) {creditForm.op === "adjust" ? "— +100 / −50" : ""}</Label>
                      <Input type="number" step="0.01" value={creditForm.amount}
                        onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                        data-testid="credit-amount-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Nota {creditForm.op === "adjust" ? "* obbligatoria" : ""}</Label>
                      <Input value={creditForm.notes}
                        onChange={(e) => setCreditForm({ ...creditForm, notes: e.target.value })}
                        placeholder="Es. ricarica semestrale"
                        data-testid="credit-notes-input" />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={submitCredit} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="credit-submit">Applica</Button>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="movements">
                <div className="max-h-[400px] overflow-y-auto border rounded-xl mt-4">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Importo</TableHead>
                      <TableHead>Saldo</TableHead><TableHead>Operatore</TableHead><TableHead>Note</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {creditData.transactions.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">Nessuna movimentazione.</TableCell></TableRow>}
                      {creditData.transactions.map((t) => (
                        <TableRow key={t.id} data-testid={`credit-tx-${t.id}`}>
                          <TableCell className="text-xs">{dateIt(t.created_at)}</TableCell>
                          <TableCell><span className={`status-pill ${txTone[t.transaction_type] || "bg-slate-100 text-slate-700"}`}>{txLabel[t.transaction_type] || t.transaction_type}</span></TableCell>
                          <TableCell className={`font-mono font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-red-600"}`}>{t.amount >= 0 ? "+" : ""}{eur(t.amount)}</TableCell>
                          <TableCell className="font-mono">{eur(t.balance_after)}</TableCell>
                          <TableCell className="text-xs text-slate-500">{t.created_by}</TableCell>
                          <TableCell className="text-xs text-slate-600 max-w-[220px] truncate" title={t.notes}>{t.notes}{t.order_number ? ` · ${t.order_number}` : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica partner" : "Nuovo partner"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Ragione sociale</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="partner-input-company" /></div>
            <div><Label>Partita IVA</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
            <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Referente</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-input-email" /></div>
            <PartnerPricingPolicy form={form} setForm={setForm} courses={courses} partners={partners} editingId={editing} />
            {!editing && <div className="col-span-2"><Label>Password (opzionale)</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Auto-generata se vuota" data-testid="partner-input-password" /></div>}
            <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Attivo</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="save-partner">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ResetPasswordDialog target={pwTarget} onClose={() => setPwTarget(null)} />
    </div>
  );
}
